// components/NoteTitleBar.js - Refactored for workspace architecture
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import styles from './NoteTitleBar.module.css';
import { useWorkspaces } from './contexts/WorkspacesContext';  // ✅ FIXED: removed extra quote
import { useCategories } from './contexts/CategoriesContext';
import { getCurrentUser } from './utils/authService';

// Helper function to format dates nicely
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  // For older dates, show the actual date
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};



// Helper function to determine badge type
const getBadgeInfo = (document) => {
  if (!document.created_at) return null;
  
  const now = new Date();
  const createdDate = new Date(document.created_at);
  const modifiedDate = new Date(document.modified_date);
  
  const timeSinceCreated = now - createdDate;
  const timeSinceModified = now - modifiedDate;
  
  const isNew = timeSinceCreated < (24 * 60 * 60 * 1000); // 24 hours
  const isRecentlyUpdated = timeSinceModified < (7 * 24 * 60 * 60 * 1000) && !isNew; // 7 days, but not new
  
  if (isNew) {
    return { type: 'new', label: 'NEW', className: styles.newBadge };
  }
  if (isRecentlyUpdated) {
    return { type: 'updated', label: 'UPDATED', className: styles.updatedBadge };
  }
  return null;
};

const NoteTitleBar = () => {
  // ✅ UPDATED: Use workspace context with correct function names
const { 
  getSelectedDocument,
  getSelectedWorkspace,        // ✅ ADD THIS
  updateWorkspaceName,
  updateDocumentTitle,
  updateWorkspaceCategory,
  selectedDocumentPublicId,
  selectedWorkspacePublicId,   // ✅ ADD THIS
  documents,
  workspaces,
  selectDocument

} = useWorkspaces();

const { categories } = useCategories();
const currentDocument = useMemo(() => getSelectedDocument(), [getSelectedDocument]);
const workspace = useMemo(() => getSelectedWorkspace(), [getSelectedWorkspace]);  // ✅ SIMPLIFIED!
  
  // Use local state to ensure immediate UI updates
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
 

  // ✅ UPDATED: Count documents in the same workspace
    const documentCount = useMemo(() => {
        if (!selectedWorkspacePublicId) return 0;
        return documents.filter(d => d.workspace_public_id === selectedWorkspacePublicId).length;
      }, [selectedWorkspacePublicId, documents]);


  
  // Update local state when the selected document changes
  const prevDocumentIdRef = useRef(null);

  useEffect(() => {
      if (workspace) {
        if (prevDocumentIdRef.current !== workspace.public_id) {
          setTitle(workspace.name || '');
          setCategoryId(workspace.primary_category_id || '');
          prevDocumentIdRef.current = workspace.public_id;
        }
      }
    }, [workspace]);
      
  if (!workspace) return null; 
  
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    // Update local state immediately
    setTitle(newTitle);
    // Update document title via context
    if (workspace?.public_id) {
        updateWorkspaceName(workspace.public_id, newTitle);
      }
  };
  
const handleCategoryChange = (e) => {
      const newCategoryId = e.target.value;
      setCategoryId(newCategoryId);
      if (selectedWorkspacePublicId) {
        updateWorkspaceCategory(selectedWorkspacePublicId, newCategoryId);
      }
    };

  // ✅ REMOVED: No more parent_id logic - documents are peers
  // Find the workspace this document belongs to
 

  
  return (
    <div className={styles.noteTitleBarWrapper}>
      <div className={styles.noteTitleBar}>
        <div className={styles.titleSection}>

          
          <input
            type="text"
            className={styles.titleInput}
            value={title}
            onChange={handleTitleChange}
            placeholder="Untitled Workspace"
          />
        </div>
        
        <select 
          className={styles.categorySelect}
          value={categoryId}
          onChange={handleCategoryChange}
        >
          {categories?.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>
      
      {/* Date information bar */}
      <div className={styles.dateInfo}>
        <div className={styles.leftInfo}>
          <span className={styles.categoryPill}>
            {categories?.find(cat => cat.id == categoryId)?.name || 'Uncategorized'}
          </span>
          
          <span className={styles.dateItem}>
            <span className={styles.dateLabel}>Created:</span>
            <span className={styles.dateValue}>{formatDate(workspace.created_at)}</span>
          </span>
          {workspace.modified_date !== workspace.created_at && (
            <span className={styles.dateItem}>
              <span className={styles.dateLabel}>Modified:</span>
              <span className={styles.dateValue}>{formatDate(workspace.modified_date)}</span>
            </span>
          )}
          <span className={styles.dateItem}>
            <span className={styles.dateLabel}>Notes:</span>
            <span className={styles.dateValue}>{workspace.note_count || 0}</span>
          </span>

          <span className={styles.dateItem}>
            <span className={styles.dateLabel}>Documents:</span>
            <span className={styles.dateValue}>{documentCount}</span>
          </span>
        </div>

        

      </div>
    </div>
  );
};

export default NoteTitleBar;